import { createSlice } from "@reduxjs/toolkit";
import { axiosInstance } from "../../../../Common/Interceptors/AxiosConfig";
import { showNotification } from "../../../../Common/Toast/Toast";

const initialState = {
  errors: null,
  isLoading: false,
  teamMembers: [],
  pendingInvitations: [],
};

const BusinessTeamSlice = createSlice({
  name: "businessTeam",
  initialState,
  reducers: {
    setErrors: (state, action) => {
      // ✅ FIX: Sanitize error to prevent React Error #31
      state.errors = typeof action.payload === 'string' ? action.payload : (action.payload?.message || "Team operation failed");
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setTeamMembers: (state, action) => {
      state.teamMembers = action.payload;
    },
    setPendingInvitations: (state, action) => {
      state.pendingInvitations = action.payload;
    },
  },
});

/**
 * Invite a member to the business
 * @param {String} businessId - Business profile ID
 * @param {Object} memberData - Member data (email, role, permissions)
 */
export const inviteMember = (businessId, memberData) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const URL = `/business/${businessId}/team/invite`;
    const response = await axiosInstance.post(URL, memberData);
    const { success, message } = response?.data || {};

    if (success) {
      showNotification({
        message: message || "Member invited successfully",
        theme: "light",
        type: "success",
      });
      // Refresh team members list
      dispatch(listBusinessMembers(businessId));
      return { success: true, message };
    } else {
      const errorMsg = message || "Failed to invite member";
      showNotification({
        message: errorMsg,
        theme: "light",
        type: "error",
      });
      return { success: false, message: errorMsg };
    }
  } catch (error) {
    const errorResponse = error?.response;
    const errorMsg =
      errorResponse?.data?.message ||
      error.message ||
      "Failed to invite member";
    dispatch(setErrors({
      status: errorResponse?.status,
      statusText: errorResponse?.statusText,
      message: errorMsg,
    }));
    showNotification({
      message: errorMsg,
      theme: "light",
      type: "error",
    });
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Accept invitation to join business
 * @param {String} businessId - Business profile ID
 * @param {String} invitationId - Invitation ID
 */
export const acceptInvitation = (businessId, invitationId) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const URL = `/business/${businessId}/team/accept/${invitationId}`; // V2 calls it token/id
    const response = await axiosInstance.patch(URL);
    const { success, message } = response?.data || {};

    if (success) {
      showNotification({
        message: message || "Invitation accepted successfully",
        theme: "light",
        type: "success",
      });
      return { success: true, message };
    } else {
      const errorMsg = message || "Failed to accept invitation";
      showNotification({
        message: errorMsg,
        theme: "light",
        type: "error",
      });
      return { success: false, message: errorMsg };
    }
  } catch (error) {
    const errorResponse = error?.response;
    const errorMsg =
      errorResponse?.data?.message ||
      error.message ||
      "Failed to accept invitation";
    dispatch(setErrors({
      status: errorResponse?.status,
      statusText: errorResponse?.statusText,
      message: errorMsg,
    }));
    showNotification({
      message: errorMsg,
      theme: "light",
      type: "error",
    });
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Reject invitation to join business
 * @param {String} businessId - Business profile ID
 * @param {String} invitationId - Invitation ID
 */
export const rejectInvitation = (businessId, invitationId) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const URL = `/api/business/${businessId}/members/reject`;
    const response = await axiosInstance.post(URL, { invitationId });
    const { status, message } = response?.data || {};

    if (status === "success") {
      showNotification({
        message: message || "Invitation rejected",
        theme: "light",
        type: "success",
      });
      return { success: true, message };
    } else {
      const errorMsg = message || "Failed to reject invitation";
      showNotification({
        message: errorMsg,
        theme: "light",
        type: "error",
      });
      return { success: false, message: errorMsg };
    }
  } catch (error) {
    const errorResponse = error?.response;
    const errorMsg =
      errorResponse?.data?.message ||
      error.message ||
      "Failed to reject invitation";
    dispatch(setErrors({
      status: errorResponse?.status,
      statusText: errorResponse?.statusText,
      message: errorMsg,
    }));
    showNotification({
      message: errorMsg,
      theme: "light",
      type: "error",
    });
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Remove a member from the business
 * @param {String} businessId - Business profile ID
 * @param {String} memberId - Member's user ID
 */
export const removeMember = (businessId, memberId) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const URL = `/business/${businessId}/team/${memberId}`;
    const response = await axiosInstance.delete(URL);
    const { success, message } = response?.data || {};

    if (success) {
      showNotification({
        message: message || "Member removed successfully",
        theme: "light",
        type: "success",
      });
      // Refresh team members list
      dispatch(listBusinessMembers(businessId));
      return { success: true, message };
    } else {
      const errorMsg = message || "Failed to remove member";
      showNotification({
        message: errorMsg,
        theme: "light",
        type: "error",
      });
      return { success: false, message: errorMsg };
    }
  } catch (error) {
    const errorResponse = error?.response;
    const errorMsg =
      errorResponse?.data?.message ||
      error.message ||
      "Failed to remove member";
    dispatch(setErrors({
      status: errorResponse?.status,
      statusText: errorResponse?.statusText,
      message: errorMsg,
    }));
    showNotification({
      message: errorMsg,
      theme: "light",
      type: "error",
    });
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Update member role and permissions
 * @param {String} businessId - Business profile ID
 * @param {String} memberId - Member's user ID
 * @param {Object} updateData - { role, permissions }
 */
export const updateMemberRole = (businessId, memberId, updateData) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const URL = `/api/business/${businessId}/members/${memberId}/role`;
    const response = await axiosInstance.put(URL, updateData);
    const { status, message } = response?.data || {};

    if (status === "success") {
      showNotification({
        message: message || "Member role updated successfully",
        theme: "light",
        type: "success",
      });
      // Refresh team members list
      dispatch(listBusinessMembers(businessId));
      return { success: true, message };
    } else {
      const errorMsg = message || "Failed to update member role";
      showNotification({
        message: errorMsg,
        theme: "light",
        type: "error",
      });
      return { success: false, message: errorMsg };
    }
  } catch (error) {
    const errorResponse = error?.response;
    const errorMsg =
      errorResponse?.data?.message ||
      error.message ||
      "Failed to update member role";
    dispatch(setErrors({
      status: errorResponse?.status,
      statusText: errorResponse?.statusText,
      message: errorMsg,
    }));
    showNotification({
      message: errorMsg,
      theme: "light",
      type: "error",
    });
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * List all team members of a business
 * @param {String} businessId - Business profile ID
 */
export const listBusinessMembers = (businessId) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const URL = `/business/${businessId}/team`;
    const response = await axiosInstance.get(URL);
    const { success, data } = response?.data || {};

    if (success) {
      // Separate active members and pending invitations
      const activeMembers = data?.filter(m => m.invitationStatus === 'accepted') || [];
      const pending = data?.filter(m => m.invitationStatus === 'pending') || [];
      
      dispatch(setTeamMembers(activeMembers));
      dispatch(setPendingInvitations(pending));
      return { success: true, data };
    } else {
      dispatch(setTeamMembers([]));
      dispatch(setPendingInvitations([]));
      return { success: false };
    }
  } catch (error) {
    const errorResponse = error?.response;
    dispatch(setErrors({
      status: errorResponse?.status,
      statusText: errorResponse?.statusText,
      message: errorResponse?.data?.message || error.message || "Something went wrong.",
    }));
    dispatch(setTeamMembers([]));
    dispatch(setPendingInvitations([]));
    return { success: false };
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Leave a business (remove yourself)
 * @param {String} businessId - Business profile ID
 */
export const leaveBusinessProfile = (businessId) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const URL = `/api/business/${businessId}/members/leave`;
    const response = await axiosInstance.post(URL);
    const { status, message } = response?.data || {};

    if (status === "success") {
      showNotification({
        message: message || "You have left the business successfully",
        theme: "light",
        type: "success",
      });
      return { success: true, message };
    } else {
      const errorMsg = message || "Failed to leave business";
      showNotification({
        message: errorMsg,
        theme: "light",
        type: "error",
      });
      return { success: false, message: errorMsg };
    }
  } catch (error) {
    const errorResponse = error?.response;
    const errorMsg =
      errorResponse?.data?.message ||
      error.message ||
      "Failed to leave business";
    dispatch(setErrors({
      status: errorResponse?.status,
      statusText: errorResponse?.statusText,
      message: errorMsg,
    }));
    showNotification({
      message: errorMsg,
      theme: "light",
      type: "error",
    });
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

export const {
  setErrors,
  setLoading,
  setTeamMembers,
  setPendingInvitations,
} = BusinessTeamSlice.actions;

export default BusinessTeamSlice.reducer;
